import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  FileEdit,
  Activity,
  UploadCloud,
  Mic,
  Users,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const TOOLS = [
  {
    title: "Email Sender",
    description: "Reach recruiters with one saved template, tracked automatically.",
    icon: Mail,
    bg: "bg-blue-50",
    iconColor: "text-blue-600",
    url: "/email-sender",
  },
  {
    title: "Email Template Editor",
    description: "Write and save the exact email every recruiter receives.",
    icon: FileEdit,
    bg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    url: "/email-editor",
  },
  {
    title: "Application Tracker",
    description: "See exactly where every application stands, status by status.",
    icon: Activity,
    bg: "bg-green-50",
    iconColor: "text-green-600",
    url: "/application-status",
  },
  {
    title: "Resume & ATS Score",
    description: "Upload your resume and see how it scores against ATS filters.",
    icon: UploadCloud,
    bg: "bg-purple-50",
    iconColor: "text-purple-600",
    url: "/resume-upload",
  },
  {
    title: "Mock Interviews",
    description: "Practice with AI-powered mock interviews before the real thing.",
    icon: Mic,
    bg: "bg-orange-50",
    iconColor: "text-orange-500",
    url: "/interviews",
  },
  {
    title: "Recruiters Directory",
    description: "Browse and connect with verified recruiters on the platform.",
    icon: Users,
    bg: "bg-rose-50",
    iconColor: "text-rose-500",
    url: "/recruiters",
  },
];

const Tools = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 mb-1.5"
          >
            <motion.span
              animate={{ rotate: [0, 12, 0, -12, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 shrink-0"
            >
              <Sparkles className="w-4 h-4 text-blue-600" />
            </motion.span>
            <h1 className="text-xl font-bold text-gray-900">Tools</h1>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="text-sm text-gray-500"
          >
            Everything you need to run your job search, in one place.
          </motion.p>
        </div>
      </div>

      {/* Tool grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TOOLS.map((tool, index) => {
            const Icon = tool.icon;
            return (
              <motion.button
                key={tool.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: (index % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(tool.url)}
                className="group relative text-left overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 hover:border-blue-200 hover:shadow-xl transition-[border-color,box-shadow]"
              >
                {/* Accent bar reveals on hover */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />

                <motion.div
                  whileHover={{ scale: 1.1, rotate: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  className={`w-12 h-12 flex items-center justify-center rounded-xl ${tool.bg} mb-4`}
                >
                  <Icon className={`w-6 h-6 ${tool.iconColor}`} />
                </motion.div>

                <p className="text-base font-semibold text-gray-900 mb-1">{tool.title}</p>
                <p className="text-sm text-gray-500 mb-4">{tool.description}</p>

                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open tool
                  <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Tools;
